/**
 * Popover — a Radix Popover on the shared cream pop-surface. The high-level
 * <Popover> takes a `trigger` and renders the content panel with an arrow.
 */
import { type ComponentPropsWithoutRef, type ReactNode } from "react";
import * as RadixPopover from "@radix-ui/react-popover";

export const PopoverRoot = RadixPopover.Root;
export const PopoverTrigger = RadixPopover.Trigger;
export const PopoverAnchor = RadixPopover.Anchor;
export const PopoverClose = RadixPopover.Close;

export interface PopoverProps
  extends Pick<
    ComponentPropsWithoutRef<typeof RadixPopover.Root>,
    "open" | "defaultOpen" | "onOpenChange" | "modal"
  > {
  /** The clickable trigger (rendered asChild). */
  readonly trigger: ReactNode;
  /** Panel content. */
  readonly children: ReactNode;
  /** Preferred side. @default "bottom" */
  readonly side?: ComponentPropsWithoutRef<typeof RadixPopover.Content>["side"];
  /** Alignment. @default "center" */
  readonly align?: ComponentPropsWithoutRef<typeof RadixPopover.Content>["align"];
  /** Offset from the trigger in px. @default 8 */
  readonly sideOffset?: number;
  /** Padding used by Radix while avoiding viewport collisions. @default 8 */
  readonly collisionPadding?: ComponentPropsWithoutRef<typeof RadixPopover.Content>["collisionPadding"];
  readonly className?: string;
}

export function Popover({
  trigger,
  children,
  side = "bottom",
  align = "center",
  sideOffset = 8,
  collisionPadding = 8,
  className,
  ...rootProps
}: PopoverProps) {
  return (
    <RadixPopover.Root {...rootProps}>
      <RadixPopover.Trigger asChild>{trigger}</RadixPopover.Trigger>
      <RadixPopover.Portal>
        <RadixPopover.Content
          side={side}
          align={align}
          sideOffset={sideOffset}
          collisionPadding={collisionPadding}
          className={["lc-pop", "lc-popover", className].filter(Boolean).join(" ")}
        >
          {children}
          <RadixPopover.Arrow className="lc-pop__arrow" width={12} height={6} />
        </RadixPopover.Content>
      </RadixPopover.Portal>
    </RadixPopover.Root>
  );
}
